/**
 * AvailableNowToggle — V1.8.2 brutalist redesign.
 *
 * "Live Mission" panel: 2px ink border, radar-pulse triple-ring dot when
 * active, monospace countdown timer, sharp sliding switch (not iOS pill).
 * Testids preserved: available-now-widget, available-now-dot,
 * available-now-toggle, available-now-remaining.
 */
import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Broadcast } from "@phosphor-icons/react";

function formatCountdown(secs) {
    if (!secs || secs <= 0) return "00:00:00";
    const h = String(Math.floor(secs / 3600)).padStart(2, "0");
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
}

export default function AvailableNowToggle() {
    const [state, setState] = useState({ boosted: false, secs: null });
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0); // force countdown redraw every 1s

    const load = useCallback(async () => {
        try {
            const res = await api.get("/daddytalk/me");
            setState({
                boosted: Boolean(res?.data?.boosted),
                secs: res?.data?.boost_secs_remaining || null,
            });
        } catch { /* silent — non-staff or offline */ }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Refresh from server every 60s (safety net + drift correction).
    useEffect(() => {
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load]);
    // Local countdown tick every second while active (no network).
    useEffect(() => {
        if (!state.boosted || !state.secs) return;
        const t = setInterval(() => setTick((x) => x + 1), 1000);
        return () => clearInterval(t);
    }, [state.boosted, state.secs]);

    const on = state.boosted;
    const remaining = state.secs && state.boosted ? Math.max(0, state.secs - tick) : 0;

    async function toggle() {
        if (busy) return;
        const next = !on;
        setBusy(true);
        try {
            const res = await api.post("/daddytalk/me/boost", { active: next });
            setTick(0);
            setState({
                boosted: Boolean(res?.data?.boosted),
                secs: res?.data?.boost_secs_remaining || null,
            });
            toast.success(next
                ? "You're LIVE — candidates see you at the top."
                : "Boost turned off.");
            window.dispatchEvent(new CustomEvent("daddytalk:my-boost-changed", {
                detail: { boosted: Boolean(res?.data?.boosted) },
            }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update");
        } finally { setBusy(false); }
    }

    return (
        <div
            data-testid="available-now-widget"
            data-active={on ? "true" : "false"}
            className={`relative rounded-2xl transition-all ${
                on
                    ? "bg-emerald-50 ring-1 ring-emerald-200"
                    : "bg-white ring-1 ring-ink/10"
            }`}
        >
            <div className="p-3">
                {/* Radar-pulse dot + label */}
                <div className="flex items-center gap-2 mb-2">
                    <span className="relative flex h-2.5 w-2.5" data-testid="available-now-dot">
                        {on && (
                            <>
                                <span className="animate-radar absolute inline-flex h-full w-full rounded-full bg-emerald-500" />
                                <span className="animate-radar-slow absolute inline-flex h-full w-full rounded-full bg-emerald-500" />
                            </>
                        )}
                        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${on ? "bg-emerald-500" : "bg-ink/25"}`} />
                    </span>
                    <p className={`text-xs font-semibold flex-1 ${on ? "text-emerald-900" : "text-ink"}`}>
                        {on ? "You're live" : "Available now"}
                    </p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        on ? "bg-emerald-500 text-white" : "bg-ink/10 text-ink/50"
                    }`}>
                        {on ? "ON" : "OFF"}
                    </span>
                </div>

                {/* Body copy or countdown */}
                <AnimatePresence mode="wait">
                    {on ? (
                        <motion.div
                            key="on"
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            className="mb-3 flex items-baseline gap-2"
                            data-testid="available-now-remaining"
                        >
                            <p className="font-mono text-lg font-bold text-emerald-700 tabular-nums">
                                {formatCountdown(remaining)}
                            </p>
                            <p className="text-[10px] text-emerald-700/70">
                                until auto-off
                            </p>
                        </motion.div>
                    ) : (
                        <motion.p
                            key="off"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-[11px] text-ink/60 leading-snug mb-3"
                        >
                            Flip on to appear at the top of the DaddyTalk drawer with a live pulse.
                        </motion.p>
                    )}
                </AnimatePresence>

                {/* Soft rounded switch */}
                <button
                    type="button"
                    onClick={toggle}
                    disabled={busy}
                    data-testid="available-now-toggle"
                    data-state={on ? "on" : "off"}
                    className={`w-full h-9 rounded-full relative overflow-hidden transition disabled:opacity-50 active:scale-[0.98] ${
                        on
                            ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                            : "bg-ink text-white hover:bg-ink/85"
                    }`}
                >
                    <span className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold">
                        <Broadcast size={13} weight="bold" />
                        {busy ? "…" : on ? "Turn off" : "Go live"}
                    </span>
                </button>
            </div>
        </div>
    );
}
